// Invitations (backend/social.py): the ones waiting for the player, and sending one. One poll for the
// whole Lobby: its « Social » tab lists them, its badge counts them, « Jouer » shows the newest.
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { sound } from "../../lib/feedback";
import { t, trServer } from "../../lib/i18n";

const POLL_MS = 4000;

/**
 * The invitations waiting for the player (GET /api/invites), polled every 4 s, with a chime when a new one
 * comes. `answer(invite, yes)`: « Rejoindre » opens the inviter's table, « Refuser » says no (the server
 * keeps that inviter away for an hour). A failed answer is said on the card (`errors[id]`); the next poll
 * takes the invitation away (the server dropped it).
 */
export function useInvites() {
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [acting, setActing] = useState({});   // id → "join" | "decline"
  const [errors, setErrors] = useState({});   // id → message
  const known = useRef(new Set());

  useEffect(() => {
    let alive = true;
    const poll = () =>
      api.get("/invites").then(({ data }) => {
        if (!alive) return;
        const list = data.invites || [];
        if (list.some((i) => !known.current.has(i.id))) sound.seat();
        known.current = new Set(list.map((i) => i.id));
        setInvites(list);
      }).catch(() => {});
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const answer = useCallback(async (invite, yes) => {
    setActing((a) => ({ ...a, [invite.id]: yes ? "join" : "decline" }));
    setErrors((e) => ({ ...e, [invite.id]: "" }));
    try {
      const { data } = await api.post(`/invites/${invite.id}/${yes ? "accept" : "decline"}`);
      setInvites((list) => list.filter((i) => i.id !== invite.id));
      if (yes) navigate(`/room/${data.room_id}`);
    } catch (e) {
      setErrors((x) => ({ ...x, [invite.id]: trServer(e?.response?.data?.detail) || t("invite.error") }));
    } finally {
      setActing((a) => ({ ...a, [invite.id]: null }));
    }
  }, [navigate]);

  return { invites, answer, acting, errors };
}

/**
 * Sending an invitation (POST /api/invites). From the Lobby a new table is opened for it and the sender
 * goes there to wait; from a waiting room it is that table. `status(userId)`: "sending", "sent", or the
 * error said by the server.
 */
export function useSendInvite() {
  const navigate = useNavigate();
  const location = useLocation();
  const [states, setStates] = useState({});

  const send = useCallback(async (userId, { onLeave } = {}) => {
    setStates((s) => ({ ...s, [userId]: "sending" }));
    try {
      const { data } = await api.post("/invites", { to_user_id: userId });
      setStates((s) => ({ ...s, [userId]: "sent" }));
      if (location.pathname !== `/room/${data.room_id}`) {
        onLeave?.();
        navigate(`/room/${data.room_id}`);
      }
    } catch (e) {
      setStates((s) => ({ ...s, [userId]: trServer(e?.response?.data?.detail) || t("players.error") }));
    }
  }, [location.pathname, navigate]);

  const status = useCallback((userId) => states[userId], [states]);
  return { send, status };
}
